import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import './ProductCard.css';
import { useAuth } from '../context/AuthContext';
import { useLocalCart } from '../context/CartContext';
import { useToast } from '../context/ToastContext';

const ProductCard = ({ product, onAdd, userRole, onEdit, onDelete, onApprove, onReject }) => {
    const navigate = useNavigate();

    const [isCheckingCarts, setIsCheckingCarts] = useState(false);
    const [activeCarts, setActiveCarts]         = useState([]);
    const [showModal, setShowModal]             = useState(false);

    const { user }      = useAuth();
    const { addToCart } = useLocalCart();
    const { addToast }  = useToast();

    const effectiveRole = userRole || user?.role;

    // Helper to close the modal and reset the button state simultaneously
    const closeWorkspaceModal = () => {
        setShowModal(false);
        setIsCheckingCarts(false);
    };

    // Lock body scroll and apply padding to prevent layout shift (flickering)
    useEffect(() => {
        if (showModal) {
            const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
            document.body.style.overflow = 'hidden';
            document.body.style.paddingRight = `${scrollBarWidth}px`;

            return () => {
                document.body.style.overflow = '';
                document.body.style.paddingRight = '';
            };
        }
    }, [showModal]);

    const status     = product?.approvalStatus || product?.status || '';
    const isPending  = status.toUpperCase() === 'PENDING';
    const isRejected = status.toUpperCase() === 'REJECTED';

    const formatCurrency = (amount) => {
        if (amount == null) return 'N/A';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0,
        }).format(amount);
    };

    const handleCardClick = () => {
        if ((isPending || isRejected) && effectiveRole !== 'ADMIN' && effectiveRole !== 'CURATOR') return;
        if (product?.id) {
            navigate(`/product/${product.id}`);
        }
    };

    const performAddToCart = () => {
        if (onAdd) {
            onAdd(product);
        } else {
            addToCart(product);
            addToast(`${product.name} added to cart`, 'success');
        }
    };

    const handleAddToCartClick = async (e) => {
        e.stopPropagation();
        e.preventDefault();

        if (!user) {
            performAddToCart();
            return;
        }

        setIsCheckingCarts(true);
        try {
            const res = await api.get('/quotes/my-carts');
            const openCarts = (res.data || []).filter(c => c.status !== 'QUOTE');
            if (openCarts.length > 0) {
                setActiveCarts(openCarts);
                // Do not reset `isCheckingCarts` here to prevent the button snap
                setShowModal(true);
            } else {
                setIsCheckingCarts(false);
                performAddToCart();
            }
        } catch {
            setIsCheckingCarts(false);
            performAddToCart();
        }
    };

    const addToSharedWorkspace = async (cartId) => {
        try {
            await api.post(`/quotes/${cartId}/add-item`, { productId: product.id, quantity: 1 });
            addToast('Added to collaborative workspace', 'success');
            closeWorkspaceModal();
        } catch (err) {
            addToast(err.response?.data?.error || 'Failed to add to workspace', 'error');
            closeWorkspaceModal();
        }
    };

    const handleEditClick    = (e) => { e.stopPropagation(); if (onEdit)    onEdit(product); };
    const handleDeleteClick  = (e) => { e.stopPropagation(); if (onDelete)  onDelete(product); };
    const handleApproveClick = (e) => { e.stopPropagation(); if (onApprove) onApprove(product.id); };
    const handleRejectClick  = (e) => { e.stopPropagation(); if (onReject)  onReject(product.id); };

    const displayImage   = product?.thumbnailUrl || product?.imageUrl || 'https://placehold.co/300x300/F9FAFB/9CA3AF?text=No+Image';
    const offerPrice     = product?.marketOperatingPriceWithGst;
    const hasDiscount    = offerPrice && offerPrice < product?.mrp;
    const discountPct    = hasDiscount ? Math.round(((product.mrp - offerPrice) / product.mrp) * 100) : 0;

    return (
        <div
            className={`product-card ${isPending ? 'is-pending' : ''} ${isRejected ? 'is-rejected' : ''}`}
            onClick={handleCardClick}
        >
            {isPending  && <div className="card-status-overlay waiting">⏳ Waiting for Approval</div>}
            {isRejected && <div className="card-status-overlay action-required">⚠️ Action Required</div>}

            <div className="product-image">
                <img src={displayImage} alt={product?.name} loading="lazy" />
                {hasDiscount && <span className="card-discount-badge">{discountPct}% OFF</span>}
            </div>

            <div className="product-info">
                <div className="product-category">
                    {typeof product?.subcategory === 'object'
                        ? product?.subcategory?.name
                        : (product?.subcategory || 'General')}
                </div>
                <h3 className="product-title">{product?.name}</h3>

                <div className="product-footer">
                    <div className="price-container">
                        {hasDiscount ? (
                            <div className="discounted-price-layout">
                                <span className="price-current">{formatCurrency(offerPrice)}</span>
                                <span className="price-original">{formatCurrency(product?.mrp)}</span>
                            </div>
                        ) : (
                            <div className="product-price">
                                {product?.mrp ? formatCurrency(product.mrp) : 'Ask for Price'}
                            </div>
                        )}
                        <div className="price-gst-label">incl. GST</div>
                    </div>

                    <div className="product-actions">
                        {effectiveRole === 'ADMIN' && isPending ? (
                            <>
                                <button className="btn-approve" onClick={handleApproveClick}>Approve</button>
                                <button className="btn-reject"  onClick={handleRejectClick}>Reject</button>
                            </>
                        ) : isRejected && effectiveRole === 'CURATOR' ? (
                            <>
                                <button className="btn-fix-resubmit" onClick={handleEditClick}>Fix & Resubmit</button>
                                <button className="btn-add-cart"     onClick={handleDeleteClick}>Delete</button>
                            </>
                        ) : (effectiveRole === 'ADMIN' || effectiveRole === 'CURATOR') ? (
                            <>
                                <button className="btn-view-details" onClick={handleEditClick}>Edit</button>
                                <button className="btn-add-cart"     onClick={handleDeleteClick}>Delete</button>
                            </>
                        ) : (
                            <>
                                <button className="btn-view-details">View</button>
                                <button
                                    className="btn-add-cart"
                                    onClick={handleAddToCartClick}
                                    disabled={isCheckingCarts}
                                >
                                    {isCheckingCarts ? '…' : 'Add to Cart'}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {showModal && ReactDOM.createPortal(
                <div
                    className="pc-modal-overlay"
                    onClick={closeWorkspaceModal}
                >
                    <div className="pc-modal" onClick={e => e.stopPropagation()}>
                        <button className="pc-modal__close" onClick={closeWorkspaceModal}>✕</button>
                        <h3 className="pc-modal__title">Where should we add this?</h3>
                        <div className="pc-modal__options">
                            <button
                                className="pc-modal__btn-personal"
                                onClick={() => { closeWorkspaceModal(); performAddToCart(); }}
                            >
                                🛒 My Personal Cart
                            </button>
                            <hr className="pc-modal__divider" />
                            <span className="pc-modal__workspace-label">Collaborative Workspaces</span>
                            {activeCarts.map(cart => (
                                <button
                                    key={cart.id}
                                    className="pc-modal__btn-workspace"
                                    onClick={() => addToSharedWorkspace(cart.id)}
                                >
                                    📁 {cart.cartName}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default ProductCard;